import React, { useState } from 'react';
import { Home, Users, ChevronLeft, ChevronRight } from 'lucide-react';
import { format, startOfWeek, addDays, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, addMonths, parse } from 'date-fns';
import { de } from 'date-fns/locale';

const KalenderApp = () => {
  const today = new Date();
  const [currentDate, setCurrentDate] = useState(today);
  const [currentView, setCurrentView] = useState('month');

  const navigateMonth = (direction) => {
    setCurrentDate(prev => addMonths(prev, direction));
  };

  const resetToToday = () => {
    setCurrentDate(today);
  };

  const WeekView = () => {
    const start = startOfWeek(currentDate, { weekStartsOn: 1 });
    const week = [...Array(7)].map((_, i) => addDays(start, i));

    return (
      <div className="grid grid-cols-7 gap-2 h-[calc(100vh-200px)]">
        {week.map((day, idx) => (
          <div key={idx} className="border rounded-lg p-2">
            <div className="text-sm font-medium mb-2">
              {format(day, 'EEEE', { locale: de })}
              <div className="text-gray-500">{format(day, 'd')}</div>
            </div>
            <div className="space-y-2">
              {isToday(day) && (
                <div className="bg-blue-50 p-2 rounded text-sm">
                  Meeting mit Kreditgeber
                  <div className="text-xs text-gray-500">9:00-11:00</div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const MonthView = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
    
    const daysToDisplay = eachDayOfInterval({
      start: startDate,
      end: endOfMonth(addMonths(startDate, 1))
    }).slice(0, 42); // Always show 6 weeks

    return (
      <div>
        <div className="grid grid-cols-7 gap-2 mb-2">
          {['MO', 'DI', 'MI', 'DO', 'FR', 'SA', 'SO'].map(day => (
            <div key={day} className="text-center font-medium text-sm">
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-2 h-[calc(100vh-250px)]">
          {daysToDisplay.map((day, idx) => (
            <div
              key={idx}
              className={`border rounded-lg p-2 min-h-[100px] ${
                !isSameMonth(day, currentDate) ? 'bg-gray-50' : ''
              } ${isToday(day) ? 'border-blue-500' : ''}`}
            >
              <div className={`text-right ${isToday(day) ? 'text-blue-500 font-bold' : ''}`}>
                {format(day, 'd')}
              </div>
              {isToday(day) && (
                <div className="mt-1 text-xs bg-red-50 p-1 rounded">
                  Heute
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const YearView = () => {
    return (
      <div className="grid grid-cols-4 gap-4">
        {[...Array(12)].map((_, idx) => {
          const monthDate = new Date(currentDate.getFullYear(), idx);
          const daysInMonth = new Date(currentDate.getFullYear(), idx + 1, 0).getDate();
          
          return (
            <div key={idx} className="border rounded-lg p-2">
              <div className="font-medium mb-2">
                {format(monthDate, 'MMMM', { locale: de })}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {[...Array(daysInMonth)].map((_, dayIdx) => {
                  const currentDay = new Date(currentDate.getFullYear(), idx, dayIdx + 1);
                  return (
                    <div 
                      key={dayIdx} 
                      className={`text-center text-xs p-1 ${
                        isToday(currentDay) ? 'bg-blue-500 text-white rounded' : ''
                      }`}
                    >
                      {dayIdx + 1}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="flex gap-4 p-4 bg-gray-50 min-h-screen">
      <div className="w-80 bg-white rounded-lg p-4 shadow">
        <button className="w-full bg-[#94CA3C] text-white rounded-lg py-2 mb-6 font-medium">
          + TERMIN HINZUFÜGEN
        </button>
      </div>

      <div className="flex-1 bg-white rounded-lg p-4 shadow">
        <div className="flex justify-between items-center mb-6">
          <button 
            className="px-6 py-1.5 rounded-full border"
            onClick={resetToToday}
          >
            Heute
          </button>
          
          <div className="flex items-center gap-4 text-red-500">
            <ChevronLeft className="cursor-pointer" onClick={() => navigateMonth(-1)} />
            <h2 className="font-medium">
              {format(currentDate, 'MMMM yyyy', { locale: de })}
            </h2>
            <ChevronRight className="cursor-pointer" onClick={() => navigateMonth(1)} />
          </div>

          <div className="flex rounded-lg border divide-x">
            <button 
              className={`px-4 py-1.5 ${currentView === 'year' ? 'bg-gray-900 text-white' : ''}`}
              onClick={() => setCurrentView('year')}
            >
              Jahr
            </button>
            <button 
              className={`px-4 py-1.5 ${currentView === 'week' ? 'bg-gray-900 text-white' : ''}`}
              onClick={() => setCurrentView('week')}
            >
              Woche
            </button>
            <button 
              className={`px-4 py-1.5 ${currentView === 'month' ? 'bg-gray-900 text-white' : ''}`}
              onClick={() => setCurrentView('month')}
            >
              Monat
            </button>
          </div>
        </div>

        <div className="overflow-auto">
          {currentView === 'month' && <MonthView />}
          {currentView === 'week' && <WeekView />}
          {currentView === 'year' && <YearView />}
        </div>
      </div>
    </div>
  );
};

export default KalenderApp;